// src/auth/strategies/OidcAuthManager.tsx
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { AuthProvider as ReactOidcAuthProvider, useAuth } from 'react-oidc-context';
import { decodeToken } from '../utils/auth';
import { useIdpInfo } from './IdpContext';
import { LoggedInUser, UserContext } from './UserContext';

const OidcLogic: React.FC<PropsWithChildren> = ({ children }) => {
  const oidcAuth = useAuth();
  const { unsetIdpInfo } = useIdpInfo();
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser>();

  useEffect(() => {
    if (!oidcAuth.isAuthenticated || !oidcAuth.user) return;
    const userId = decodeToken(oidcAuth.user.access_token).sub!;
    setLoggedInUser({ userId, userAccessToken: oidcAuth.user.access_token });
  }, [oidcAuth.isAuthenticated, oidcAuth.user]);

  const contextValue = useMemo(
    () => ({
      loggedInUser,
      isLoading: oidcAuth.isLoading,
      login: async () => oidcAuth.signinRedirect(),
      logout: async () => {
        setLoggedInUser(undefined);
        unsetIdpInfo();
        await oidcAuth.signoutRedirect({ post_logout_redirect_uri: window.location.origin });
      },
      isSelfSignedToken: () => false,
      signoutOnUnauthorized: () => {}, // Implement if needed
    }),
    [loggedInUser, oidcAuth, unsetIdpInfo],
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

// The Wrapper that sets up the Library
export const OidcAuthManager: React.FC<PropsWithChildren> = ({ children }) => {
  const { idpInfo } = useIdpInfo();

  const userManager = useMemo(() => {
    if (!idpInfo || idpInfo.algorithm !== 'rs-256') return null;
    return new UserManager({
      automaticSilentRenew: true,
      authority: idpInfo.authority,
      client_id: idpInfo.clientId,
      redirect_uri: idpInfo.redirectUri,
      scope: idpInfo.scope,
      response_type: 'code',
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      extraQueryParams: { audience: idpInfo.audience },
    });
  }, [idpInfo]);

  // Handle your custom event listener
  useEffect(() => {
    if (!userManager) return () => {};
    const cb = () => window.history.replaceState({}, document.title, window.location.pathname);
    userManager.events.addUserLoaded(cb);
    return () => {
      userManager.events.removeUserLoaded(cb);
    };
  }, [userManager]);

  if (!userManager) return null;

  return (
    <ReactOidcAuthProvider userManager={userManager}>
      <OidcLogic>{children}</OidcLogic>
    </ReactOidcAuthProvider>
  );
};
